import { useCallback } from 'react'
import type { Node, NodeChange } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'

type SetNodes = React.Dispatch<React.SetStateAction<Node<ReactFlowNodeData>[]>>

interface UseNodeClickHandlersDeps {
  setNodes: SetNodes
  onNodesChange: (changes: NodeChange<Node<ReactFlowNodeData>>[]) => void
}

export function useNodeClickHandlers({ setNodes, onNodesChange }: UseNodeClickHandlersDeps) {
  /**
   * Handles clicking on a node.
   * Shows the toolbar for the clicked node and hides toolbars for all other nodes.
   * Optimized to only update nodes where toolbar visibility actually changes.
   */
  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.stopPropagation()

      setNodes((currentNodes) => {
        // Find nodes that need changes
        const clickedNode = currentNodes.find((n) => n.id === node.id)
        const nodesWithVisibleToolbar = currentNodes.filter(
          (n) => n.id !== node.id && n.data.toolbarVisible === true
        )

        // Check if clicked node already has toolbar visible and no others need hiding
        if (clickedNode?.data.toolbarVisible === true && nodesWithVisibleToolbar.length === 0) {
          return currentNodes
        }

        // Build set of node IDs that need updates
        const nodeIdsToUpdate = new Set<string>()
        if (clickedNode?.data.toolbarVisible !== true) {
          nodeIdsToUpdate.add(node.id)
        }
        nodesWithVisibleToolbar.forEach((n) => nodeIdsToUpdate.add(n.id))

        // Only create new objects for nodes that actually change
        return currentNodes.map((n) => {
          if (!nodeIdsToUpdate.has(n.id)) {
            return n // Return same reference - no change
          }
          return {
            ...n,
            data: {
              ...n.data,
              toolbarVisible: n.id === node.id,
            },
          }
        })
      })
    },
    [setNodes]
  )

  /**
   * Handles clicking on the empty canvas (pane).
   * Hides all node toolbars.
   * Optimized to only update nodes that have visible toolbars.
   */
  const handlePaneClick = useCallback(() => {
    setNodes((nodes) => {
      // Find nodes that have visible toolbars
      const nodesWithVisibleToolbar = nodes.filter((n) => n.data.toolbarVisible === true)

      // If no toolbars are visible, return same array reference
      if (nodesWithVisibleToolbar.length === 0) {
        return nodes
      }

      // Build set of node IDs that need updates
      const nodeIdsToUpdate = new Set(nodesWithVisibleToolbar.map((n) => n.id))

      // Only create new objects for nodes that actually change
      return nodes.map((n) => {
        if (!nodeIdsToUpdate.has(n.id)) {
          return n // Return same reference - no change
        }
        return {
          ...n,
          data: {
            ...n.data,
            toolbarVisible: false,
          },
        }
      })
    })
  }, [setNodes])

  /**
   * Handles clicking on an edge.
   * Currently just prevents event propagation - edge selection is handled automatically by React Flow.
   */
  const handleEdgeClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    // Edge selection is handled automatically by React Flow
  }, [])

  /**
   * Handles React Flow node changes (position, selection, etc.).
   */
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes as NodeChange<Node<ReactFlowNodeData>>[])
    },
    [onNodesChange]
  )

  return { handleNodeClick, handlePaneClick, handleEdgeClick, handleNodesChange }
}
